import React from 'react';
import PropTypes from 'prop-types';

import { WeezHeader, Card, Icon, Table, InfoText, Message, ContentCard, Filter, FilterSetController, Status, Paginator, Tooltip } from '@weezevent/nacre';
import SubscriptionTicket from '../../../models/subscriptionTicket';
import Subscription from '../../../models/subscription';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import useAppContext from '../../../context/useAppContext';
import DataModal from './components/dataModal';
import LoadingTable from '../../../components/loadingTable';
import Styles from './ticketList.module.css';
import SubscriptionLink from '../../../models/subscriptionLink';

function TicketStatus({ ticket }) {
    const { t } = useTranslation();

    const status = React.useMemo(() => {
        if (ticket.status === 'done') {
            return {
                color: 'green',
                text: t('ticket.webservice.list.status-sent'),
                tooltip: t('ticket.webservice.list.status-sent-tooltip')
            };
        }
        if (ticket.status === 'unfiltered') {
            return {
                color: 'red',
                text: t('ticket.webservice.list.status-unfiltered'),
                tooltip: t('ticket.webservice.list.status-unfiltered-tooltip')
            };
        }
        if (ticket.status === 'pending' && !ticket.last_pushed_to_weez) {
            return {
                color: 'orange',
                text: t('ticket.webservice.list.status-pending'),
                tooltip: t('ticket.webservice.list.status-pending-tooltip')
            };
        }
        if (ticket.status === 'pending' && ticket.last_pushed_to_weez) {
            return {
                color: 'violet',
                text: t('ticket.webservice.list.status-updating'),
                tooltip: t('ticket.webservice.list.status-updating-tooltip')
            };
        }
    }, [ticket]);

    return (
        <Tooltip title={status.tooltip} triggerOnHover={true}>
            <Status className={Styles['subscription-status']} {...status} />
        </Tooltip>
    );
}

function HeaderRow() {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader>
            <th>{t('ticket.webservice.ticket-list.header.created')}</th>
            <th>{t('ticket.webservice.ticket-list.header.barcode')}</th>
            <th>{t('ticket.webservice.ticket-list.header.status')}</th>
            <th className={Styles['width-x-small']}>{t('ticket.webservice.ticket-list.header.ticket-data')}</th>
        </Table.Tr>
    );
}

function TableRow({ ticket, timezone, onSelectSubscriptionTicket }) {
    const locale = i18n.language;

    const date = React.useMemo(() => {
        const formated_date = new Date(ticket.created_at).toLocaleString(locale, { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
        const formated_hour = new Date(ticket.created_at).toLocaleString(locale, { timeZone: timezone, hour: '2-digit', minute: '2-digit' });
        return <InfoText subtitle={formated_hour}>{formated_date}</InfoText>;
    }, [ticket, timezone]);

    return (
        <Table.Tr>
            <Table.Td>{date}</Table.Td>
            <Table.Td>
                <InfoText subtitle={ticket.barcode_id}>{ticket.ticket_identifier}</InfoText>
            </Table.Td>
            <Table.Td>
                <TicketStatus ticket={ticket} />
            </Table.Td>
            <Table.Td className={Styles['td-centered']}>
                <Icon name="eye" onClick={() => onSelectSubscriptionTicket(ticket)} />
            </Table.Td>
        </Table.Tr>
    );
}
TableRow.propTypes = {
    ticket: PropTypes.object,
    timezone: PropTypes.string.isRequired,
    onSelectSubscriptionTicket: PropTypes.func.isRequired
};

export default function TicketList({ organizationId, subscriptionId }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const [selectedSubscriptionTicket, setSelectedSubscriptionTicket] = React.useState(null);
    const [filter, setFilter] = React.useState({ search: '', status: new Set() });

    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 100, offset: 0 }));
    const [queryParams, setQueryParams] = React.useState({ search: '', status__in: '', ...defaultPagination });

    const [subscriptionTickets] = SubscriptionTicket.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            subscriptionId: subscriptionId,
            query: queryParams
        },
        [organizationId, subscriptionId, queryParams]
    );

    const [subscription, loadingSubscription] = Subscription.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            id: subscriptionId
        },
        [organizationId, subscriptionId, queryParams]
    );

    const [subscriptionLink] = SubscriptionLink.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId,
            subscriptionId
        },
        [organizationId, subscriptionId]
    );

    const onCloseModal = React.useCallback(() => {
        setSelectedSubscriptionTicket(null);
    }, []);

    const totalCount = React.useMemo(() => {
        return Number.parseInt(subscriptionTickets?.__meta?.headers.get('total-count')) || 0;
    }, [subscriptionTickets]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);
            setQueryParams({ ...queryParams, offset: queryParams.limit * value });
        },
        [queryParams]
    );

    /** TODO Wait until the filter is operational on the back side */
    const filteredTickets = React.useMemo(() => {
        if (!subscriptionTickets?.length) {
            return [];
        }

        return subscriptionTickets;
    }, [subscriptionTickets]);

    const options = [
        { key: 'unfiltered', text: 'unfiltered', name: 'unfiltered' },
        { key: 'pending', text: 'pending', name: 'pending' },
        { key: 'done', text: 'sent', name: 'done' },
        { key: 'updating', text: 'updating', name: 'updating' }
    ];

    let onChangeFilter = _filter => {
        if (!_filter.search) {
            _filter.search = '';
        }
        if (!_filter.status) {
            _filter.status = '';
        }

        let paramChange = new Set();

        const statusString = Array.from(_filter.status).join(',');
        if (statusString !== queryParams['status__in']) {
            paramChange = { ...paramChange, status__in: Array.from(_filter.status).join(',') };
        }

        if (_filter.search !== queryParams['search']) {
            paramChange = { ...paramChange, search: _filter.search };
        }

        if (paramChange.size !== 0) {
            setQueryParams({ ...queryParams, ...paramChange });
        }
    };

    return (
        <div>
            <WeezHeader title={subscription?.name} />
            {!subscriptionLink?.length && subscriptionTickets?.length > 0 ? (
                <Message
                    title={t('ticket.webservice.ticket-list.message.title')}
                    type="danger"
                    links={[
                        {
                            label: t('ticket.webservice.ticket-list.message.link'),
                            href: `/ticket/O${organizationId}/webservices/${subscriptionId}/mapping`
                        },
                        {
                            label: t('ticket.webservice.ticket-list.message.rule-link'),
                            href: `/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links`
                        }
                    ]}
                >
                    {t('ticket.webservice.ticket-list.message.body')}
                </Message>
            ) : !subscriptionTickets?.length && !subscriptionLink?.length ? (
                <Message title={t('ticket.manager.attendees.noAttendees')} type="danger">
                    {t('ticket.webservice.ticket-list.empty-card-body')}
                </Message>
            ) : null}
            <DataModal subscription={selectedSubscriptionTicket} show={Boolean(selectedSubscriptionTicket)} onClose={onCloseModal} />
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={totalCount}
                        labelTotal={t('ticket.common.attendee', { count: totalCount })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            onChange: (synth, value) => {
                                let _filter = { ...filter, search: value };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            handleReset: () => {
                                let _filter = { ...filter, status: new Set(), search: '' };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filter.search || ''
                        }}
                        onChange={values => {
                            let _filter = { ...filter, status: values.status };
                            setFilter(_filter);
                            onChangeFilter(_filter);
                        }}
                        activeReset
                        resetLabel={{ content: t('ticket.common.action.reset') }}
                    >
                        <Filter
                            name="status"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            multiple
                            options={options}
                            index="1"
                        />
                    </FilterSetController>
                </ContentCard>
                <Table useNew className={Styles['attendees_table']}>
                    <Table.THead>
                        <HeaderRow />
                    </Table.THead>
                    <Table.TBody>
                        {filteredTickets.map((ticket, index) => (
                            <TableRow key={index} ticket={ticket} timezone={organization.timezone} onSelectSubscriptionTicket={setSelectedSubscriptionTicket} />
                        ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loadingSubscription} />
                {!loadingSubscription && !filteredTickets.length && (
                    <Card.EmptyState title={t('ticket.manager.attendees.noAttendees')} subtitle={t('ticket.manager.attendees.noAttendeesSubtitle')} icon={<Icon name="user" />} />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={queryParams.limit} totalCount={totalCount} />
                </div>
            </Card>
        </div>
    );
}
TicketList.propTypes = {
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};
